/**
 * Счёт из 1С в PDF (21.10.2026, docs/features/unf-invoice-upload.md): текст PDF —
 * разрозненные надписи с координатами. Здесь они раскладываются в строки и ячейки,
 * как в Excel-счёте, и дальше идут в тот же разбор `parseUnfInvoice`.
 * Чистая функция: сам PDF читает сайт (pdf.js), правила раскладки — здесь.
 */

export interface PdfTextItem {
  page: number
  /** Левый край надписи. */
  x: number
  /** Высота над нижним краем страницы — чем больше, тем выше. */
  y: number
  width: number
  str: string
}

type Line = PdfTextItem[]

/** Надписи одной строки: на одной странице и с разницей по высоте не больше 4 pt. */
const SAME_LINE = 4
/** Подпись шапки в две строки («Производи-» / «тель») — в пределах 8 pt от шапки. */
const HEAD_BAND = 8
/** Промежуток между надписями, после которого вне таблицы начинается новая ячейка. */
const CELL_GAP = 6

const clean = (s: string): string => s.replace(/\u00a0/g, ' ').trim()

function toLines(items: PdfTextItem[]): Line[] {
  const sorted = items
    .filter((it) => clean(it.str) !== '')
    .sort((a, b) => a.page - b.page || b.y - a.y || a.x - b.x)
  const lines: Line[] = []
  for (const it of sorted) {
    const last = lines[lines.length - 1]
    const first = last?.[0]
    if (last && first && first.page === it.page && Math.abs(first.y - it.y) <= SAME_LINE) {
      last.push(it)
    } else lines.push([it])
  }
  for (const line of lines) line.sort((a, b) => a.x - b.x)
  return lines
}

/** Строка вне таблицы: соседние надписи склеиваются, большой промежуток — новая ячейка. */
function plainCells(line: Line): string[] {
  const cells: string[] = []
  let end = -Infinity
  for (const it of line) {
    if (cells.length > 0 && it.x - end <= CELL_GAP) {
      cells[cells.length - 1] = `${cells[cells.length - 1]} ${clean(it.str)}`
    } else cells.push(clean(it.str))
    end = it.x + it.width
  }
  return cells
}

const isHeadLine = (line: Line): boolean => {
  const s = line.map((it) => clean(it.str))
  return s.includes('№') && s.some((c) => c.startsWith('Товар')) && s.includes('Сумма')
}

interface Column {
  title: string
  left: number
  right: number
}

/** Колонки по шапке; подписи в две строки, стоящие одна над другой, — одна колонка. */
function headColumns(band: PdfTextItem[]): Column[] {
  const cols: Column[] = []
  for (const it of [...band].sort((a, b) => b.y - a.y || a.x - b.x)) {
    const right = it.x + it.width
    const same = cols.find((c) => it.x < c.right && right > c.left)
    if (same) {
      same.title = `${same.title}${clean(it.str)}`
      same.left = Math.min(same.left, it.x)
      same.right = Math.max(same.right, right)
    } else cols.push({ title: clean(it.str), left: it.x, right })
  }
  return cols.sort((a, b) => a.left - b.left)
}

/**
 * Надписи PDF → строки ячеек для `parseUnfInvoice`. В таблице надпись попадает в
 * колонку по середине, граница колонок — посередине между подписями шапки. Номер
 * строки — первая надпись-число, кончающаяся у правого края колонки «№». Строка таблицы без номера —
 * перенос названия или производителя предыдущей строки.
 */
export function pdfTextToRows(items: PdfTextItem[]): string[][] {
  const lines = toLines(items)
  const headAt = lines.findIndex(isHeadLine)
  if (headAt < 0) return lines.map(plainCells)
  const headLine = lines[headAt] ?? []
  const headY = headLine[0]?.y ?? 0
  const headPage = headLine[0]?.page ?? 0
  const inBand = (l: Line) =>
    l[0]?.page === headPage && Math.abs((l[0]?.y ?? 0) - headY) <= HEAD_BAND
  const cols = headColumns(lines.filter(inBand).flat())
  const noIdx = cols.findIndex((c) => c.title === '№')
  const nameIdx = cols.findIndex((c) => c.title.startsWith('Товар'))
  const manIdx = cols.findIndex((c) =>
    c.title.replace(/[-\s]/g, '').toLowerCase().startsWith('производи'),
  )
  const qtyIdx = cols.findIndex((c) => c.title.startsWith('Кол'))
  const unitIdx = cols.findIndex((c) => c.title.startsWith('Ед'))
  const textCols = new Set([nameIdx, manIdx].filter((i) => i >= 0))
  const bounds = cols.slice(1).map((c, i) => ((cols[i]?.right ?? 0) + c.left) / 2)
  const colOf = (it: PdfTextItem) => {
    const mid = it.x + it.width / 2
    let i = 0
    while (i < bounds.length && mid > (bounds[i] ?? Infinity)) i++
    return i
  }
  // Номер строки выровнен по правому краю колонки «№»; перенос названия из одних
  // цифр («1767054») начинается в колонке «Товар» и номером не считается.
  const noRight = (cols[noIdx]?.right ?? 0) + 4

  const rows: string[][] = []
  let inTable = false
  let lastRow: string[] | null = null
  for (const [i, line] of lines.entries()) {
    if (isHeadLine(line)) {
      rows.push(cols.map((c) => c.title))
      inTable = true
      lastRow = null
      continue
    }
    const headBand =
      line[0] &&
      lines.some(
        (h, j) =>
          j !== i &&
          isHeadLine(h) &&
          h[0]?.page === line[0]?.page &&
          Math.abs((h[0]?.y ?? 0) - (line[0]?.y ?? 0)) <= HEAD_BAND,
      )
    if (headBand) continue
    const plain = plainCells(line)
    if (!inTable || plain.some((c) => /^Итого:?$/i.test(c))) {
      if (plain.some((c) => /^Итого:?$/i.test(c))) inTable = false
      rows.push(plain)
      continue
    }
    const cells = cols.map(() => '')
    let rest = line
    const first = line[0]
    if (first && /^\d+$/.test(clean(first.str)) && first.x + first.width <= noRight) {
      cells[noIdx] = clean(first.str)
      rest = line.slice(1)
    }
    for (const it of rest) {
      // Короткое название («R125») серединой левее границы — всё равно «Товар»:
      // в колонке «№» только номер строки.
      const c = colOf(it) === noIdx ? nameIdx : colOf(it)
      cells[c] = cells[c] ? `${cells[c]} ${clean(it.str)}` : clean(it.str)
    }
    // «1 шт» одной надписью — количество и единица.
    const qty = qtyIdx >= 0 ? cells[qtyIdx]?.match(/^([\d\s.,]+?)\s+([^\d\s].*)$/) : null
    if (qty && unitIdx >= 0 && !cells[unitIdx]) {
      cells[qtyIdx] = qty[1] ?? ''
      cells[unitIdx] = qty[2] ?? ''
    }
    if (cells[noIdx]) {
      rows.push(cells)
      lastRow = cells
    } else if (lastRow) {
      // Перенос: дописываем только текстовые колонки — числа без номера строки
      // (итоги по колонкам) к позиции не относятся.
      for (const c of textCols) {
        if (cells[c]) lastRow[c] = lastRow[c] ? `${lastRow[c]} ${cells[c]}` : (cells[c] ?? '')
      }
    }
  }
  return rows
}
